import {
  Briefcase,
  Wallet,
  Receipt,
  Calculator,
  CheckCircle,
  WarningCircle,
} from "@phosphor-icons/react";
import type { InvestmentItemWithCalculations } from "screens/investment/types/investment";

export type BudgetFilter =
  | "totaal"
  | "ruwGeschat"
  | "gedetailleerd"
  | "opgenomen"
  | "werkelijkeUitgegeven";

export interface BudgetBucket {
  rows: InvestmentItemWithCalculations[];
  total: number;
  /** Optional sub-amount rendered as a small overflow label under the tile. */
  overflowAmount?: number;
  /** Custom copy for the overflow sub-label (defaults to "overflow"). */
  overflowLabel?: string;
  /**
   * Optional plain-text subline rendered below the tile total. Takes
   * precedence over `overflowAmount` / `overflowLabel` when set. Used for
   * non-overflow context lines (e.g. "−80% onder strategisch budget").
   */
  subline?: { text: string; tone?: "amber" | "emerald" | "neutral" };
}

export type BudgetBuckets = Record<BudgetFilter, BudgetBucket>;

interface BudgetPipelineFilterProps {
  active: BudgetFilter;
  onChange: (filter: BudgetFilter) => void;
  buckets: BudgetBuckets;
  /**
   * Som van de goedgekeurde offertes op dit gebouw, voor de groep *Werkelijke
   * kostprognose*. Komt van de backend (`totals.totalApprovedQuotes`), niet uit
   * de investeringslijnen: offertes hangen aan het gebouw, niet aan een lijn.
   */
  approvedQuotesTotal?: number;
}

const formatCurrency = (value: number) =>
  new Intl.NumberFormat("nl-BE", {
    style: "currency",
    currency: "EUR",
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(value);

interface PillConfig {
  key: BudgetFilter;
  label: string;
  icon: React.ReactNode;
  activeClasses: string;
  inactiveClasses: string;
}

interface GroupConfig {
  title: string;
  pills: PillConfig[];
}

// Layout: three logical groups, ordered left → right.
//   1. Budget planning over gebouw      — top-down strategic capacity
//   2. Budget uitwerking in projecten   — what's been planned / committed
//   3. Werkelijke uitgegeven            — actual invoices on the building
const GROUPS: GroupConfig[] = [
  {
    title: "Budget planning over gebouw",
    pills: [
      {
        key: "totaal",
        label: "Strategisch budget totaal",
        icon: <Wallet className="h-4 w-4" weight="fill" />,
        activeClasses: "bg-neutral-800 text-white border-neutral-800",
        inactiveClasses:
          "bg-neutral-50 text-neutral-700 border-neutral-200 hover:bg-neutral-100",
      },
    ],
  },
  {
    title: "Budget uitwerking in projecten",
    pills: [
      {
        key: "ruwGeschat",
        label: "Projectbudget",
        icon: <Calculator className="h-4 w-4" weight="fill" />,
        activeClasses: "bg-indigo-600 text-white border-indigo-600",
        inactiveClasses:
          "bg-indigo-50 text-indigo-700 border-indigo-200 hover:bg-indigo-100",
      },
      {
        key: "opgenomen",
        label: "Projectonderdelen detailbudget",
        icon: <Briefcase className="h-4 w-4" weight="fill" />,
        activeClasses: "bg-orange-600 text-white border-orange-600",
        inactiveClasses:
          "bg-orange-50 text-orange-700 border-orange-200 hover:bg-orange-100",
      },
    ],
  },
  {
    title: "Gefactureerd",
    pills: [
      {
        key: "werkelijkeUitgegeven",
        label: "Gefactureerd",
        icon: <Receipt className="h-4 w-4" weight="fill" />,
        activeClasses: "bg-emerald-600 text-white border-emerald-600",
        inactiveClasses:
          "bg-emerald-50 text-emerald-700 border-emerald-200 hover:bg-emerald-100",
      },
    ],
  },
];

// Niet-aanklikbare tegel met dezelfde vorm als de filterknoppen. Voor cijfers die
// je wél moet zien maar waarop niet gefilterd wordt — ze leiden af uit gegevens die
// niet per investeringslijn bestaan (offertes hangen aan het gebouw).
interface ReadOnlyPillProps {
  label: string;
  icon: React.ReactNode;
  containerClasses: string;
  /** Currency value shown on the second line. Defaults to 0. */
  total?: number;
  /** Optional helper line under the value. */
  subline?: string;
  /** Tooltip; leg hier uit waar het cijfer vandaan komt. */
  title?: string;
}

const ReadOnlyPill = ({
  label,
  icon,
  containerClasses,
  total = 0,
  subline,
  title,
}: ReadOnlyPillProps) => (
  <div
    className={`flex min-w-[180px] cursor-not-allowed items-center gap-3 rounded-lg border px-3 py-2 text-left opacity-90 ${containerClasses}`}
    title={title}
    aria-disabled="true"
  >
    <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-white/60">
      {icon}
    </span>
    <span className="flex flex-col leading-tight">
      <span className="text-xs font-medium opacity-90">{label}</span>
      <span className="text-sm font-semibold">{formatCurrency(total)}</span>
      {subline && (
        <span className="text-[10px] font-medium text-neutral-secondary-mid">
          {subline}
        </span>
      )}
    </span>
  </div>
);

interface StrategischDetailPillProps {
  total: number;
  /** Bv. "waarvan € 155.000 acquisitiebudget" — zie `subline` op de bucket. */
  subline?: string;
}

// Read-only mirror of the "Strategisch" column total from the investment
// lines table (= sum of per-line `min(est, bucket.totalAmount || est)`,
// equal to `buckets.gedetailleerd.total`).
const StrategischDetailPill = ({ total, subline }: StrategischDetailPillProps) => (
  <ReadOnlyPill
    label="Strategisch budget gedetailleerd"
    icon={<Calculator className="h-4 w-4" weight="fill" />}
    containerClasses="border-neutral-200 bg-neutral-50 text-neutral-700"
    total={total}
    subline={subline}
    title="Som van de kolom 'Strategisch' uit de budgetlijnen — enkel het deel van de strategische budgetten dat aan een investeringslijn hangt. Het acquisitiebudget telt hier dus mee zodra er lijnen op staan, en niet zolang dat niet zo is."
  />
);

/**
 * Verfijnt het projectonderdelen-detailbudget in twee schijven: het deel dat al
 * vastligt in **goedgekeurde offertes** (een echte prijs) en de rest, die nog op
 * een **schatting** steunt. Samen geven ze de meest actuele kostindicatie —
 * schatting eerst, gaandeweg scherper naarmate er offertes goedgekeurd worden.
 *
 * Deze twee stonden hardgecodeerd op € 0 met "binnenkort beschikbaar", ook op
 * gebouwen met goedgekeurde offertes. Ze komen nu uit de gegevens:
 * `goedgekeurdeOffertes` is de som van de goedgekeurde offertes op het gebouw,
 * en het ongedekte deel is wat er van het detailbudget overblijft.
 */
const WerkelijkeKostprognoseGroup = ({
  goedgekeurdeOffertes,
  detailbudget,
}: {
  goedgekeurdeOffertes: number;
  detailbudget: number;
}) => {
  // Nooit negatief: is er voor méér goedgekeurd dan er in het detailbudget zit,
  // dan is er niets "ongedekt" — dat is een overschrijding, en die staat al bij
  // de detailbudget-tegel als overflow.
  const ongedekt = Math.max(0, detailbudget - goedgekeurdeOffertes);
  const overschrijding = Math.max(0, goedgekeurdeOffertes - detailbudget);

  return (
    <div className="flex flex-col gap-1.5">
      <span className="text-[10px] font-semibold uppercase tracking-wide text-neutral-secondary-mid">
        Werkelijke kostprognose
      </span>
      <div className="flex flex-wrap items-stretch gap-2">
        <ReadOnlyPill
          label="Goedgekeurde offertes"
          icon={<CheckCircle className="h-4 w-4" weight="fill" />}
          containerClasses="border-emerald-200 bg-emerald-50 text-emerald-700"
          total={goedgekeurdeOffertes}
          subline={
            overschrijding > 0
              ? `${formatCurrency(overschrijding)} boven het detailbudget`
              : undefined
          }
          title="Som van de goedgekeurde offertes op dit gebouw — verfijnt het detailbudget met werkelijke prijzen"
        />
        <ReadOnlyPill
          label="Project detailbudget ongedekt door offertes"
          icon={<WarningCircle className="h-4 w-4" weight="fill" />}
          containerClasses="border-amber-200 bg-amber-50 text-amber-800"
          total={ongedekt}
          title="Detailbudget min goedgekeurde offertes — het deel dat nog op een schatting steunt"
        />
      </div>
    </div>
  );
};

export const BudgetPipelineFilter = ({
  active,
  onChange,
  buckets,
  approvedQuotesTotal = 0,
}: BudgetPipelineFilterProps) => {
  // Split groups so the "Werkelijke kostprognose" placeholder group can land
  // directly left of the "Gefactureerd" group. The "Strategisch budget
  // gedetailleerd" placeholder pill lives inside the planning group and is
  // appended in renderGroup below.
  const gefactureerdIdx = GROUPS.findIndex((g) => g.title === "Gefactureerd");
  const groupsBeforeFactuur =
    gefactureerdIdx >= 0 ? GROUPS.slice(0, gefactureerdIdx) : GROUPS;
  const groupsFromFactuur =
    gefactureerdIdx >= 0 ? GROUPS.slice(gefactureerdIdx) : [];

  const renderGroup = (group: GroupConfig) => (
    <div key={group.title} className="flex flex-col gap-1.5">
      <span className="text-[10px] font-semibold uppercase tracking-wide text-neutral-secondary-mid">
        {group.title}
      </span>
      <div className="flex flex-wrap items-stretch gap-2">
        {group.pills.map((pill) => {
              const isActive = active === pill.key;
              const bucket = buckets[pill.key];
              const showOverflow =
                !bucket?.subline &&
                bucket?.overflowAmount !== undefined &&
                bucket.overflowAmount > 0;
              const sublineToneClass =
                bucket?.subline?.tone === "emerald"
                  ? "text-emerald-700"
                  : bucket?.subline?.tone === "neutral"
                    ? "text-neutral-secondary-mid"
                    : "text-amber-700";
              return (
                <button
                  key={pill.key}
                  type="button"
                  onClick={() => onChange(pill.key)}
                  className={`flex min-w-[180px] items-center gap-3 rounded-lg border px-3 py-2 text-left transition-colors ${
                    isActive ? pill.activeClasses : pill.inactiveClasses
                  }`}
                  title={`Filter op ${pill.label.toLowerCase()}`}
                >
                  <span
                    className={`flex h-8 w-8 shrink-0 items-center justify-center rounded-full ${
                      isActive ? "bg-white/20" : "bg-white/60"
                    }`}
                  >
                    {pill.icon}
                  </span>
                  <span className="flex flex-col leading-tight">
                    <span className="text-xs font-medium opacity-90">
                      {pill.label}
                    </span>
                    <span className="text-sm font-semibold">
                      {formatCurrency(bucket.total)}
                    </span>
                    {bucket?.subline ? (
                      <span
                        className={`text-[10px] font-medium ${sublineToneClass}`}
                      >
                        {bucket.subline.text}
                      </span>
                    ) : showOverflow ? (
                      <span className="text-[10px] font-medium text-amber-700">
                        +{formatCurrency(bucket.overflowAmount!)}{" "}
                        {bucket.overflowLabel ?? "overflow"}
                      </span>
                    ) : null}
                  </span>
                </button>
              );
            })}
        {group.title === "Budget planning over gebouw" && (
          <StrategischDetailPill
            total={Number(buckets.gedetailleerd?.total) || 0}
            subline={buckets.gedetailleerd?.subline?.text}
          />
        )}
      </div>
    </div>
  );

  return (
    <div className="flex flex-wrap items-stretch gap-4">
      {groupsBeforeFactuur.map(renderGroup)}
      <WerkelijkeKostprognoseGroup
        goedgekeurdeOffertes={approvedQuotesTotal}
        // Het "ongedekte" deel wordt afgezet tegen het projectonderdelen-
        // detailbudget: dat is de post die de offertes vervangen.
        detailbudget={Number(buckets.opgenomen?.total) || 0}
      />
      {groupsFromFactuur.map(renderGroup)}
    </div>
  );
};
